import React, { useContext } from 'react';

import { withCustomPaths, withProductTemplateThumbnails, withURL } from '@lucid/utils';
import { useSmartlingStrings } from '@lucid/hooks';
import { theme } from '@lucid/theme';

import Box from '../box/box';
import Container from '../container/container';
import Heading from '../heading/heading';
import LayoutContext from '../context/layout-context';
import Link from '../link/link';
import TemplateCard from '../cards/template-card/template-card';
import type { Lucid } from '../../types/types';

interface ParentCategorySectionProps {
  /** An array of category objects. */
  categories: Lucid.Components.ProductTemplate.Category[];
  /** Optional additional class name for styling. */
  className?: string;
}

const ParentCategorySection: React.FC<ParentCategorySectionProps> = ({ categories, className }) => {
  const { urlLangPrefix, shortCode, product } = useContext(LayoutContext);
  const baseHubPath = withCustomPaths(shortCode, product, 'product-template-category');
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_consideration_template',
      'mktg_product_template_hub_used_with',
      'mktg_product_template_hub_view_more_like_this',
    ],
    shortCode,
  });

  type ProcessedCategory = Pick<Lucid.Components.ProductTemplate.Category, 'name' | 'title' | 'slug'> & {
    templates: Lucid.Components.ProductTemplate.Data[];
  };

  const processedCategories = categories.reduce((allCategories, currentCategory): ProcessedCategory[] => {
    const childTemplates = currentCategory.childrenProductTemplates;

    let firstThree = [] as Lucid.Components.ProductTemplate.Data[];

    // TODO: This is a temporary solution to get the first three templates for each category
    // I'm sure we'll want better criteria for the templates to display here
    while (firstThree.length < 3) {
      const match = childTemplates?.find(
        t => t.shortCode === shortCode && !firstThree.some(f => f.metadata?.slug === t.metadata?.slug),
      );

      if (match) {
        firstThree = [...firstThree, match];
      } else {
        break;
      }
    }

    return [
      ...allCategories,
      { name: currentCategory.name, title: currentCategory.title, slug: currentCategory.slug, templates: firstThree },
    ];
  }, [] as ProcessedCategory[]);

  return (
    <Container
      as="section"
      css={theme => ({ [theme.breakpoints.bp768.max]: { padding: 0 } })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Box css={{ maxWidth: '1200px', margin: '0 auto' }}>
        {processedCategories?.map(({ name, slug, title, templates }) => (
          <Box key={slug} css={{ width: '100%', marginBottom: theme.space.s64 }}>
            <Box
              className="parent-category-sub-section"
              css={theme => ({
                backgroundColor: theme.colors.coolGrey[5],
                padding: theme.space.s40,
                borderRadius: theme.radii.r12,
              })}
            >
              <Box
                css={theme => ({
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  gap: theme.space.s32,
                  [theme.breakpoints.bp900.min]: { flexDirection: 'row' },
                })}
              >
                <Heading
                  as="h2"
                  css={theme => ({
                    marginBottom: 0,
                    [theme.breakpoints.bp900.min]: { marginBottom: theme.space.s40 },
                  })}
                  variant="hero2XL700"
                >
                  {title || name}
                </Heading>
                <Box
                  css={theme => ({
                    marginBottom: theme.space.s24,
                    flex: '1 0 auto',
                    [theme.breakpoints.bp900.min]: { textAlign: 'right' },
                  })}
                >
                  <Link
                    aria-label={`${name} ${withSmartling('mktg_product_template_hub_view_more_like_this')}`}
                    href={withURL(`${baseHubPath}/${slug}`, { urlLangPrefix, product, willPrependLangPrefix: true })}
                    iconAnimation="navigational"
                    variant="primaryAlternate"
                  >
                    {withSmartling('mktg_product_template_hub_view_more_like_this')}
                  </Link>
                </Box>
              </Box>
              <Box
                css={theme => ({
                  display: 'grid',
                  justifyItems: 'center',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  '& > *:nth-of-type(n+4)': {
                    display: 'none',
                  },
                  gap: theme.space.s24,
                  [theme.breakpoints.bp1024.max]: {
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    '& > *:nth-of-type(n+3)': {
                      display: 'none',
                    },
                  },
                  [theme.breakpoints.bp768.max]: {
                    gridTemplateColumns: '1fr',
                    '& > *:nth-of-type(n+2)': {
                      display: 'none',
                    },
                  },
                })}
              >
                {templates.map(template => {
                  const {
                    Document: { title, id, product_id },
                    metadata,
                  } = template;

                  const image = {
                    imageAlt: withSmartling('mktg_consideration_template', {
                      template: title,
                    }),
                    imageSrc: withProductTemplateThumbnails({
                      templateId: id,
                      params: {
                        thumbnailSize: 516,
                      },
                    }),
                  };

                  return (
                    <TemplateCard
                      key={`${name}-${id}`}
                      heading={metadata?.marketingTitle || title}
                      integrationText={`${withSmartling('mktg_product_template_hub_used_with')}:`}
                      link={{
                        final_url: withURL(`${baseHubPath}/${metadata?.slug}`, {
                          urlLangPrefix,
                          product,
                          willPrependLangPrefix: true,
                        }),
                        accessibleDescription: withSmartling('mktg_consideration_template', {
                          template: metadata?.marketingTitle || '',
                        }),
                      }}
                      media={{
                        image: {
                          filename_disk: image.imageSrc,
                        } as Lucid.Directus.Generated.Directus_Files,
                      }}
                      productId={product_id}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Container>
  );
};

export default ParentCategorySection;
